import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Bell, Loader2, CheckCircle, XCircle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card'
import { useSelector } from 'react-redux'
import type { RootState } from '@/lib/redux/store'
import Helper from '@/lib/helper'
import { toast } from 'sonner'
import api from '@/lib/axiosInstance'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useNavigate } from 'react-router-dom'
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"

interface Notification {
    id: number;
    title: string;
    remarks: string;
    path?: string;
    readAt: string | null;
    createdAt: string;
    json?: string | null;
    type?: string;
}

interface PendingAction {
    notification: Notification;
    actionKey: string;
    actionUrl: string;
}

const Dashboard = () => {
    const { agent } = useSelector((state: RootState) => state.auth);
    const navigate = useNavigate();
    const [activeListingsCount, setActiveListingsCount] = useState<number>(0);
    const [pendingListingsCount, setPendingListingsCount] = useState<number>(0);
    const [expiredListingsCount, setExpiredListingsCount] = useState<number>(0);
    const [showSheet, setShowSheet] = useState<boolean>(false);
    
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loadingNotifications, setLoadingNotifications] = useState<boolean>(false);
    const [hasMoreNotifications, setHasMoreNotifications] = useState<boolean>(true);
    const [notificationPage, setNotificationPage] = useState<number>(1);
    const [processingActions, setProcessingActions] = useState<Set<number>>(new Set());
    const observerTarget = useRef<HTMLDivElement>(null);
    
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
    
    const unreadCount = notifications.filter(n => !n.readAt).length;

    const getNotifications = async (page: number) => {
        if (loadingNotifications) return;
        
        try {
            setLoadingNotifications(true);
            const response = await api.get(`/me/notifications?page=${page}&limit=50`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            
            const newNotifications = response.data.notifications || [];
            
            if (page === 1) {
                setNotifications(newNotifications);
            } else {
                setNotifications(prev => [...prev, ...newNotifications]);
            }
            
            setHasMoreNotifications(newNotifications.length === 50);
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setLoadingNotifications(false);
        }
    }

    const handleActionClick = async (notification: Notification, actionKey: string, actionUrl: string) => {
        // Show confirmation dialog before processing
        setPendingAction({ notification, actionKey, actionUrl });
        setShowConfirmDialog(true);
    }

    const confirmAction = async () => {
        if (!pendingAction) return;
        
        const { notification, actionUrl } = pendingAction;
        
        try {
            setProcessingActions(prev => new Set(prev).add(notification.id));
            setShowConfirmDialog(false);
            
            const response = await api.post(actionUrl);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            
            toast.success('Action completed successfully');
            // Keep buttons disabled after success
        } catch (err: any) {
            setProcessingActions(prev => {
                const newSet = new Set(prev);
                newSet.delete(notification.id);
                return newSet;
            });
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setPendingAction(null);
        }
    }

    const cancelAction = () => {
        setShowConfirmDialog(false);
        setPendingAction(null);
    }

    const isApproveAction = (actionKey: string) => {
        return actionKey.toLowerCase().includes('approve') || actionKey.toLowerCase().includes('accept');
    }

    const isDeclineAction = (actionKey: string) => {
        return actionKey.toLowerCase().includes('decline') || actionKey.toLowerCase().includes('reject');
    }

    const isNotificationAction = (actionKey: string, actionUrl: unknown) => {
        if (typeof actionUrl !== 'string' || !actionUrl.startsWith('/')) return false;
        return isApproveAction(actionKey) || isDeclineAction(actionKey);
    }

    const isCoShareAction = (notification: Notification) => {
        return notification.type === 'coShareInvitation' || notification.type === 'coShareRequest';
    }

    const markNotificationsAsRead = async (notificationIds: number[]) => {
        if (notificationIds.length === 0) return;
        
        try {
            await api.post('/me/notifications/read', 
                { notificationIds }, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            
            setNotifications(prev => prev.map(notification => 
                notificationIds.includes(notification.id) 
                    ? { ...notification, readAt: new Date().toISOString() }
                    : notification
            ));
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            console.error('Failed to mark notifications as read:', error);
        }
    }

    const handleNotificationClick = (notification: Notification, hasActions: boolean) => {
        if (!notification.readAt) {
            markNotificationsAsRead([notification.id]);
        }

        if (notification.path) {
            setShowSheet(false);
            navigate(`/${notification.path}`);
        }
    }

    const loadMoreNotifications = useCallback(() => {
        if (hasMoreNotifications && !loadingNotifications) {
            setNotificationPage(prev => prev + 1);
        }
    }, [hasMoreNotifications, loadingNotifications]);

    useEffect(() => {
        if (notificationPage > 1) {
            getNotifications(notificationPage);
        }
    }, [notificationPage]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting) {
                    loadMoreNotifications();
                }
            },
            { threshold: 0.1 }
        );

        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [loadMoreNotifications]);

    useEffect(() => {
        if (showSheet && notifications.length > 0) {
            const unreadIds = notifications
                .filter(n => !n.readAt)
                .map(n => n.id);
            
            if (unreadIds.length > 0) {
                const timeoutId = setTimeout(() => {
                    markNotificationsAsRead(unreadIds);
                }, 1000);
                
                return () => clearTimeout(timeoutId);
            }
        }
    }, [showSheet, notifications]);

    useEffect(() => {
        const getListingsData = async () => {
            try {
                const response = await api.get(`/me/listings`);
                if(!response.data.status) {
                    throw new Error(response.data.error);
                }
                setActiveListingsCount(response.data.activeListings);
                setPendingListingsCount(response.data.pendingListings);
                setExpiredListingsCount(response.data.expiredListings);
            } catch (err: any) {
                const error = Helper.errorHandler(err);
                toast.error(error);
            }
        }

        getListingsData();
        getNotifications(1);
    }, []);

    return agent && (
        <div className='space-y-5 sm:space-y-6'>
            <div className='flex flex-col gap-3 flex-row items-center justify-between'>
                <div>
                    <h5 className='h5'>DASHBOARD</h5>
                </div>
                <div className='flex items-center gap-2 self-start sm:self-auto'>
                    <Sheet open={showSheet} onOpenChange={setShowSheet}>
                        <SheetTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className='relative'>
                                <Bell size={18} className={unreadCount > 0 ? 'text-red-500' : ''} />
                                {
                                    unreadCount > 0 && (
                                        <Badge 
                                            variant="destructive" 
                                            className='absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs animate-pulse'>
                                            {unreadCount > 9 ? '9+' : unreadCount}
                                        </Badge>
                                    )
                                }
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
                            <SheetHeader className="p-6 border-b">
                                <SheetTitle className="flex items-center gap-2">
                                    <Bell size={20} />
                                    Notifications
                                </SheetTitle>
                                <SheetDescription>
                                    {unreadCount > 0 
                                        ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` 
                                        : 'You\'re all caught up!'}
                                </SheetDescription>
                            </SheetHeader>
                            
                            <div className='flex-1 overflow-y-auto'>
                                {
                                    notifications.length === 0 && !loadingNotifications ? (
                                        <div className='flex flex-col items-center justify-center h-full p-8 text-gray-400'>
                                            <Bell size={64} className='mb-4 opacity-20' />
                                            <p className='text-lg font-medium'>No notifications</p>
                                            <p className='text-sm text-center mt-2'>
                                                You don't have any notifications yet.
                                            </p>
                                        </div>
                                    ) : (
                                        <>
                                            {notifications.map((notification) => {
                                                // Parse JSON first to determine if it has actions
                                                let payload: { [key: string]: unknown } = {};
                                                try {
                                                    if (notification.json) {
                                                        payload = JSON.parse(notification.json);
                                                    }
                                                } catch (e) {
                                                    // Invalid JSON, no actions
                                                }
                                                
                                                const actions = Object.fromEntries(
                                                    Object.entries(payload).filter(([key, value]) => isNotificationAction(key, value))
                                                ) as { [key: string]: string };
                                                const hasActions = Object.keys(actions).length > 0;
                                                const isProcessed = processingActions.has(notification.id);
                                                
                                                return (
                                                    <div
                                                        key={notification.id}
                                                        onClick={() => handleNotificationClick(notification, hasActions)}
                                                        className={`p-5 border-b transition-colors ${
                                                            notification.readAt 
                                                                ? 'bg-white hover:bg-gray-50' 
                                                                : 'bg-blue-50 hover:bg-blue-100'
                                                        } ${notification.path ? 'cursor-pointer' : ''}`}
                                                    >
                                                        <div className='flex gap-3'>
                                                            {!notification.readAt && (
                                                                <div className='flex-shrink-0 pt-1'>
                                                                    <div className='w-2 h-2 rounded-full bg-blue-500'></div>
                                                                </div>
                                                            )}
                                                            
                                                            <div className='flex-1 min-w-0'>
                                                                <h6 className={`text-sm mb-1 ${notification.readAt ? 'font-medium' : 'font-semibold'}`}>
                                                                    {notification.title}
                                                                </h6>
                                                                <p className='text-sm text-gray-600 mb-2'>
                                                                    {notification.remarks}
                                                                </p>
                                                                <span className='text-xs text-gray-400'>
                                                                    {Helper.formatDateTime(notification.createdAt)}
                                                                </span>

                                                                {hasActions && (
                                                                    <div className="flex gap-2 mt-3 flex-wrap">
                                                                        {Object.entries(actions).map(([key, url]) => (
                                                                            <Button 
                                                                                key={key}
                                                                                size="sm" 
                                                                                variant={key.toLowerCase().includes('approve') || key.toLowerCase().includes('accept') ? 'default' : 'outline'}
                                                                                className="flex-1 min-w-[100px] transition-all duration-300"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleActionClick(notification, key, url);
                                                                                }}
                                                                                disabled={isProcessed}>
                                                                                {key.toLowerCase().includes('approve') || key.toLowerCase().includes('accept') ? (
                                                                                    <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                                                                ) : key.toLowerCase().includes('decline') || key.toLowerCase().includes('reject') ? (
                                                                                    <XCircle className="w-3.5 h-3.5 mr-1" />
                                                                                ) : null}
                                                                                {key}
                                                                            </Button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            
                                            <div ref={observerTarget} className='h-4'>
                                                {loadingNotifications && (
                                                    <div className='flex justify-center py-4'>
                                                        <Loader2 className='w-6 h-6 animate-spin text-primary' />
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )
                                }
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>

            <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {pendingAction && isApproveAction(pendingAction.actionKey) && isCoShareAction(pendingAction.notification)
                                ? 'Co-Share Agreement'
                                : 'Confirm Action'}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="space-y-3">
                            {pendingAction && isApproveAction(pendingAction.actionKey) && isCoShareAction(pendingAction.notification) ? (
                                <>
                                    <p>By approving this co-share request, you agree to share 50% of the commission/profit with the listing agent.</p>
                                    <p className="font-medium text-gray-900">Do you agree to these terms?</p>
                                </>
                            ) : (
                                <p>Are you sure you want to proceed with this action?</p>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={cancelAction}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={confirmAction}>
                            {pendingAction && isApproveAction(pendingAction.actionKey) && isCoShareAction(pendingAction.notification)
                                ? 'I Agree'
                                : 'Confirm'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <h3 className='mt-3'>Welcome back, </h3>
            <h5 className='font-semibold'>{agent.firstName} {agent.lastName}</h5>
            
            <div className='mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3'>
                <div className='w-full'>
                    <Card>
                        <CardContent className='p-5'>
                            <CardTitle className='text-center text-sm'>Active Listings</CardTitle>
                            <CardDescription className='text-center'>{ activeListingsCount }</CardDescription>
                        </CardContent>
                    </Card>
                </div>
                <div className='w-full'>
                    <Card>
                        <CardContent className='p-5'>
                            <CardTitle className='text-center text-sm'>Pending Listings</CardTitle>
                            <CardDescription className='text-center'>{ pendingListingsCount }</CardDescription>
                        </CardContent>
                    </Card>
                </div>
                {/* <div className='md:w-1/3 lg:w-1/4 w-full p-1'>
                    <Card>
                        <CardContent>
                            <CardTitle className='text-center text-sm'>Expired Listings</CardTitle>
                            <CardDescription className='text-center'>{ expiredListingsCount }</CardDescription>
                        </CardContent>
                    </Card>
                </div> */}
            </div>
        </div>
    )
}

export default Dashboard
